import * as dayjs from 'dayjs';
import { TimeZone } from '../../time/timezone.js';
import { MapUtils } from '../../utility/map-utils.js';
import {
  DataModel, DataModelBuilder, isSameData, SerializationContext, SerializedData,
} from '../dto/dto.js';
import { SyncEvent, SyncEventSource } from '../sync-event.js';
import { fromEnumAsString, toEnumFromString } from '../../serialization/enum-utils.js';
import { OpenAccessPass } from './open-access-pass/open-access-pass.js';

export class OpenAccessPassCreatedEvent extends SyncEvent {
  static readonly TYPE_ID: string = '70158308f54347e18e7028fbd2c88572';

  private constructor(
    public readonly uniqueId: string,
    public readonly creatorId: string | null,
    public readonly deviceId: string | null,
    public readonly creationDate: dayjs.Dayjs,
    public readonly source: SyncEventSource,
    public readonly pass: OpenAccessPass,
  ) {
    super();
  }

  static create(args: {
    uniqueId: string,
    creatorId: string | null,
    deviceId: string | null,
    creationDate: dayjs.Dayjs,
    source: SyncEventSource,
    pass: OpenAccessPass,
  }): OpenAccessPassCreatedEvent {
    return new OpenAccessPassCreatedEvent(
        args.uniqueId,
        args.creatorId,
        args.deviceId,
        args.creationDate,
        args.source,
        args.pass,
    );
  }

  get $typeId(): string {
    return OpenAccessPassCreatedEvent.TYPE_ID;
  }

  static fromMap(context: SerializationContext, map: SerializedData): OpenAccessPassCreatedEvent {
    return new OpenAccessPassCreatedEvent(
        MapUtils.get<string>(map, 'uniqueId'),
        MapUtils.getOrNull<string>(map, 'creatorId'),
        MapUtils.getOrNull<string>(map, 'deviceId'),
        context.decodeDate(MapUtils.get<number>(map, 'creationDate')),
        toEnumFromString(MapUtils.get<string>(map, 'source'), SyncEventSource),
        context.fromData<OpenAccessPass>(MapUtils.get<SerializedData>(map, 'pass')),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'uniqueId': this.uniqueId,
      'creatorId': this.creatorId,
      'deviceId': this.deviceId,
      'creationDate': context.encodeDate(this.creationDate),
      'source': fromEnumAsString(this.source),
      'passId': this.pass.uniqueId,
      'pass': context.toData(this.pass),
    };
  }

  clone(): this {
    return new OpenAccessPassCreatedEvent(
        this.uniqueId,
        this.creatorId,
        this.deviceId,
        TimeZone.cloneDate(this.creationDate),
        this.source,
        this.pass.clone(),
    ) as this;
  }

  isSame(other: OpenAccessPassCreatedEvent): boolean {
    if(this === other) {
      return true;
    }

    return other.uniqueId === this.uniqueId &&
        other.creatorId === this.creatorId &&
        other.deviceId === this.deviceId &&
        other.creationDate === this.creationDate &&
        other.source === this.source &&
        isSameData(other.pass, this.pass);
  }

  static verifyType(dm: DataModel): boolean {
    return dm instanceof OpenAccessPassCreatedEvent;
  }

  static assertType(dm: DataModel): dm is OpenAccessPassCreatedEvent {
    return dm instanceof OpenAccessPassCreatedEvent;
  }
}

export class OpenAccessPassCreatedEventBuilder extends DataModelBuilder<OpenAccessPassCreatedEvent> {
  build(context: SerializationContext, map: SerializedData): OpenAccessPassCreatedEvent {
    return OpenAccessPassCreatedEvent.fromMap(context, map);
  }
}
